import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import type {
    AssemblyDocument,
    ModelAssetFile,
    ModelAssetNode,
    ProjectModelLoadResult,
    ProjectModelSceneBinding,
} from '../../project-model';
import type {AssemblyMateDocumentJSON} from '../../assembly';
import {cloneAssemblyDocument} from '../../project-model';
import {buildProjectModelDesignTreeProjection} from '../../project-model/design-tree/build-project-model-design-tree-projection';
import {isSerializablePlainData} from './SerializablePlainData';

/**
 * 装配结构历史 adapter ID。
 */
export const ASSEMBLY_STRUCTURE_HISTORY_ADAPTER_ID = 'history-domain:assembly-structure';

/**
 * 装配结构历史实体 ID。
 *
 * 设计说明：当前编辑器一次只打开一个根装配工程，所以装配结构使用根级单实体快照恢复，
 * 避免把模型树、sceneBindings、装配文档索引和 working 文档拆成多个补丁后产生半恢复状态。
 */
export const ASSEMBLY_STRUCTURE_HISTORY_ENTITY_ID = 'assembly-structure';

/**
 * 可序列化的模型资产文件快照。
 */
export interface AssemblyStructureModelAssetFileSnapshot {
    /** 文件在版本提交内的路径。 */
    readonly fileId: string;
    /** 文件业务 UUID。 */
    readonly uuid: string;
    /** 文件类型。 */
    readonly kind: string;
    /** 下载 URL。 */
    readonly url: string;
    /** 文件在项目模型加载流程中的职责。 */
    readonly role: ModelAssetFile['role'];
    /** 项目模型文件的显式加载模式。 */
    readonly loadMode?: ModelAssetFile['loadMode'];
    /** complete GLB 当前版本包的业务根选择身份。 */
    readonly completeGlbRootInstanceId?: string;
}

/**
 * 可序列化的模型资产节点快照。
 */
export interface AssemblyStructureModelAssetNodeSnapshot {
    /** 节点稳定 key。 */
    readonly nodeKey: string;
    /** 节点展示名。 */
    readonly displayName?: string;
    /** OpenProject/CadNode 返回的实例 UUID。 */
    readonly instanceUuid?: string;
    /** 父节点 key。 */
    readonly parentNodeKey?: string;
    /** 节点深度。 */
    readonly depth: number;
    /** 节点所属版本仓库 ID。 */
    readonly repositoryId: string;
    /** 节点当前分支 ID。 */
    readonly branchId: string;
    /** 节点所属提交 ID。 */
    readonly commitId: string;
    /** 当前节点版本身份。 */
    readonly versionKey: string;
    /** 当前节点是否允许作为版本切换边界。 */
    readonly versionSwitchable: boolean;
    /** 当前节点类型。 */
    readonly nodeType: ModelAssetNode['nodeType'];
    /** 当前节点相对父节点的 4x4 局部变换矩阵。 */
    readonly localTransform: readonly number[];
    /** 当前节点包围盒。 */
    readonly boundingBox: readonly number[];
    /** 当前节点的装配恢复信息文件。 */
    readonly assemblyManifest?: AssemblyStructureModelAssetFileSnapshot;
    /** 当前节点自身的可加载包文件。 */
    readonly packageFiles: readonly AssemblyStructureModelAssetFileSnapshot[];
    /** 当前节点的直接子节点。 */
    readonly children: readonly AssemblyStructureModelAssetNodeSnapshot[];
}

/**
 * 装配文档索引快照条目。
 */
export interface AssemblyStructureDocumentEntrySnapshot {
    /** 装配文档所属 nodeKey。 */
    readonly nodeKey: string;
    /** 装配文档快照。 */
    readonly document: AssemblyDocument;
}

/**
 * 装配删除子树 payload 引用。
 */
export interface AssemblyStructureDeletedSubtreePayloadRef {
    /** payload 稳定 ID。 */
    readonly payloadId: string;
    /** payload 所在浏览器存储层级。 */
    readonly storageTier: 'opfs' | 'indexeddb';
}

/**
 * 装配结构历史快照。
 */
export interface AssemblyStructureHistorySnapshot {
    /** 根装配节点 key。 */
    readonly rootNodeKey: string;
    /** 项目模型根节点快照。 */
    readonly root: AssemblyStructureModelAssetNodeSnapshot;
    /** 场景对象到项目模型节点的绑定。 */
    readonly sceneBindings: readonly ProjectModelSceneBinding[];
    /** 项目节点到运行时 occurrence 与组件根的不可变绑定快照。 */
    readonly runtimeBindings: readonly PuppyCAD.ProjectModelRuntimeBinding[];
    /** 装配文档索引快照。 */
    readonly assemblyDocumentsByNodeKey: readonly AssemblyStructureDocumentEntrySnapshot[];
    /** 当前根装配的 working_assembly.json 快照。 */
    readonly workingAssemblyDocument: AssemblyDocument;
    /** 与装配结构同步变更的 Mate 文档快照；仅删除/切换等会影响配合悬挂引用的结构事务携带。*/
    readonly assemblyMateDocument?: AssemblyMateDocumentJSON;
    /** 删除组件时被移出的大型场景子树 payload 引用；非删除类结构快照可为空。 */
    readonly deletedSubtreePayloadRef?: AssemblyStructureDeletedSubtreePayloadRef;
}

/**
 * 装配结构历史运行时存储。
 */
export interface AssemblyStructureHistoryStore {
    /**
     * 暂存装配结构历史恢复目标。
     *
     * @param snapshot 目标装配结构快照。
     */
    readonly stageAssemblyStructureHistoryRestore: (snapshot: AssemblyStructureHistorySnapshot) => void;
}

/**
 * 复制模型资产文件快照，明确丢弃 `size` 这类非 JSON 主状态字段。
 *
 * @param file 模型资产文件。
 * @returns 可写入历史的文件快照。
 */
function createModelAssetFileSnapshot(file: ModelAssetFile): AssemblyStructureModelAssetFileSnapshot {
    return {
        fileId: file.fileId,
        uuid: file.uuid,
        kind: file.kind,
        url: file.url,
        role: file.role,
        ...(file.loadMode === undefined ? {} : {loadMode: file.loadMode}),
        ...(file.completeGlbRootInstanceId === undefined
            ? {}
            : {completeGlbRootInstanceId: file.completeGlbRootInstanceId}),
    };
}

/**
 * 从快照恢复模型资产文件。
 *
 * @param snapshot 文件快照。
 * @returns 模型资产文件。
 */
function restoreModelAssetFile(snapshot: AssemblyStructureModelAssetFileSnapshot): ModelAssetFile {
    return {
        fileId: snapshot.fileId,
        uuid: snapshot.uuid,
        kind: snapshot.kind,
        url: snapshot.url,
        role: snapshot.role,
        ...(snapshot.loadMode === undefined ? {} : {loadMode: snapshot.loadMode}),
        ...(snapshot.completeGlbRootInstanceId === undefined
            ? {}
            : {completeGlbRootInstanceId: snapshot.completeGlbRootInstanceId}),
    };
}

/**
 * 创建可序列化模型资产节点快照。
 *
 * @param node 模型资产节点。
 * @returns 模型资产节点快照。
 */
function createModelAssetNodeSnapshot(node: ModelAssetNode): AssemblyStructureModelAssetNodeSnapshot {
    return {
        nodeKey: node.nodeKey,
        ...(node.displayName === undefined ? {} : {displayName: node.displayName}),
        ...(node.instanceUuid === undefined ? {} : {instanceUuid: node.instanceUuid}),
        ...(node.parentNodeKey === undefined ? {} : {parentNodeKey: node.parentNodeKey}),
        depth: node.depth,
        repositoryId: node.repositoryId,
        branchId: node.branchId,
        commitId: node.commitId,
        versionKey: node.versionKey,
        versionSwitchable: node.versionSwitchable,
        nodeType: node.nodeType,
        localTransform: [...node.localTransform],
        boundingBox: [...node.boundingBox],
        ...(node.assemblyManifest === undefined ? {} : {
            assemblyManifest: createModelAssetFileSnapshot(node.assemblyManifest),
        }),
        packageFiles: node.packageFiles.map(createModelAssetFileSnapshot),
        children: node.children.map(createModelAssetNodeSnapshot),
    };
}

/**
 * 从快照恢复模型资产节点。
 *
 * @param snapshot 模型资产节点快照。
 * @returns 模型资产节点。
 */
function restoreModelAssetNode(snapshot: AssemblyStructureModelAssetNodeSnapshot): ModelAssetNode {
    return {
        nodeKey: snapshot.nodeKey,
        ...(snapshot.displayName === undefined ? {} : {displayName: snapshot.displayName}),
        ...(snapshot.instanceUuid === undefined ? {} : {instanceUuid: snapshot.instanceUuid}),
        ...(snapshot.parentNodeKey === undefined ? {} : {parentNodeKey: snapshot.parentNodeKey}),
        depth: snapshot.depth,
        repositoryId: snapshot.repositoryId,
        branchId: snapshot.branchId,
        commitId: snapshot.commitId,
        versionKey: snapshot.versionKey,
        versionSwitchable: snapshot.versionSwitchable,
        nodeType: snapshot.nodeType,
        localTransform: [...snapshot.localTransform],
        boundingBox: [...snapshot.boundingBox],
        ...(snapshot.assemblyManifest === undefined ? {} : {
            assemblyManifest: restoreModelAssetFile(snapshot.assemblyManifest),
        }),
        packageFiles: snapshot.packageFiles.map(restoreModelAssetFile),
        children: snapshot.children.map(restoreModelAssetNode),
    };
}

/**
 * 克隆 Mate 文档历史快照。
 *
 * 设计说明：
 * 装配结构历史只保存纯 JSON，不持有 Mate controller、Viewer 或 Object3D 引用；这里使用 JSON 结构化克隆，
 * 保证 Undo/Redo 暂存快照不会被运行时后续编辑反向污染。
 *
 * @param document Mate 文档快照。
 * @returns 克隆后的 Mate 文档快照。
 */
function cloneAssemblyMateDocumentSnapshot(document: AssemblyMateDocumentJSON): AssemblyMateDocumentJSON {
    return JSON.parse(JSON.stringify(document)) as AssemblyMateDocumentJSON;
}

/**
 * 归一化装配结构历史快照。
 *
 * @param snapshot 来源快照。
 * @returns 可序列化装配结构快照。
 */
function normalizeAssemblyStructureHistorySnapshot(
    snapshot: AssemblyStructureHistorySnapshot,
): AssemblyStructureHistorySnapshot {
    return {
        rootNodeKey: snapshot.rootNodeKey,
        root: createModelAssetNodeSnapshot(restoreModelAssetNode(snapshot.root)),
        sceneBindings: snapshot.sceneBindings.map((binding) => ({
            ...binding,
        })),
        runtimeBindings: snapshot.runtimeBindings.map((binding) => ({
            ...binding,
        })),
        assemblyDocumentsByNodeKey: snapshot.assemblyDocumentsByNodeKey.map((entry) => ({
            nodeKey: entry.nodeKey,
            document: cloneAssemblyDocument(entry.document),
        })),
        workingAssemblyDocument: cloneAssemblyDocument(snapshot.workingAssemblyDocument),
        ...(snapshot.assemblyMateDocument ? {
            assemblyMateDocument: cloneAssemblyMateDocumentSnapshot(snapshot.assemblyMateDocument),
        } : {}),
        ...(snapshot.deletedSubtreePayloadRef ? {
            deletedSubtreePayloadRef: {
                payloadId: snapshot.deletedSubtreePayloadRef.payloadId,
                storageTier: snapshot.deletedSubtreePayloadRef.storageTier,
            },
        } : {}),
    };
}

/**
 * 归一化历史实体快照。
 *
 * @param snapshot 来源实体快照。
 * @returns 可序列化实体快照。
 */
function normalizeAssemblyStructureEntitySnapshot(
    snapshot: HistoryEntitySnapshot<AssemblyStructureHistorySnapshot>,
): HistoryEntitySnapshot<AssemblyStructureHistorySnapshot> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeAssemblyStructureHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断装配结构快照是否为可序列化 plain data。
 *
 * @param snapshot 装配结构快照。
 * @returns 是否可安全写入历史。
 */
export function isPlainAssemblyStructureHistorySnapshot(snapshot: AssemblyStructureHistorySnapshot): boolean {
    return typeof snapshot.rootNodeKey === 'string'
        && snapshot.root.nodeKey === snapshot.rootNodeKey
        && isSerializablePlainData(normalizeAssemblyStructureHistorySnapshot(snapshot));
}

/**
 * 从项目模型加载结果创建装配结构历史快照。
 *
 * @param input 当前项目模型和根装配 working 文档。
 * @returns 可写入历史的装配结构快照。
 */
export function createAssemblyStructureHistorySnapshot(input: {
    readonly loadResult: ProjectModelLoadResult;
    readonly workingAssemblyDocument: AssemblyDocument;
    readonly assemblyMateDocument?: AssemblyMateDocumentJSON;
    readonly deletedSubtreePayloadRef?: AssemblyStructureDeletedSubtreePayloadRef;
}): AssemblyStructureHistorySnapshot {
    return normalizeAssemblyStructureHistorySnapshot({
        rootNodeKey: input.loadResult.root.nodeKey,
        root: createModelAssetNodeSnapshot(input.loadResult.root),
        sceneBindings: input.loadResult.sceneBindings.map((binding) => ({...binding})),
        runtimeBindings: Array.from(input.loadResult.runtimeState.bindingsByNodeKey.values())
            .map((binding) => ({...binding})),
        assemblyDocumentsByNodeKey: Array.from(input.loadResult.assemblyDocumentsByNodeKey.entries())
            .map(([nodeKey, document]) => ({
                nodeKey,
                document: cloneAssemblyDocument(document),
            })),
        workingAssemblyDocument: cloneAssemblyDocument(input.workingAssemblyDocument),
        ...(input.assemblyMateDocument ? {
            assemblyMateDocument: cloneAssemblyMateDocumentSnapshot(input.assemblyMateDocument),
        } : {}),
        ...(input.deletedSubtreePayloadRef ? {
            deletedSubtreePayloadRef: input.deletedSubtreePayloadRef,
        } : {}),
    });
}

/**
 * 从装配结构快照恢复项目模型加载结果。
 *
 * @param snapshot 装配结构历史快照。
 * @returns 项目模型加载结果。
 */
export function createProjectModelLoadResultFromAssemblyStructureHistorySnapshot(
    snapshot: AssemblyStructureHistorySnapshot,
): ProjectModelLoadResult {
    const normalizedSnapshot = normalizeAssemblyStructureHistorySnapshot(snapshot);
    const root = restoreModelAssetNode(normalizedSnapshot.root);
    const assemblyDocumentsByNodeKey = new Map(
        normalizedSnapshot.assemblyDocumentsByNodeKey.map((entry) => [
            entry.nodeKey,
            cloneAssemblyDocument(entry.document),
        ] as const),
    );

    assemblyDocumentsByNodeKey.set(
        normalizedSnapshot.rootNodeKey,
        cloneAssemblyDocument(normalizedSnapshot.workingAssemblyDocument),
    );

    return {
        root,
        sceneBindings: normalizedSnapshot.sceneBindings.map((binding) => ({...binding})),
        runtimeState: {
            rootNodeKey: normalizedSnapshot.rootNodeKey,
            bindingsByNodeKey: new Map(
                normalizedSnapshot.runtimeBindings.map((binding) => [
                    binding.nodeKey,
                    Object.freeze({...binding}),
                ] as const),
            ),
        },
        designTree: buildProjectModelDesignTreeProjection({
            root,
            sceneBindings: normalizedSnapshot.sceneBindings,
        }),
        assemblyDocumentsByNodeKey,
    };
}

/**
 * 应用装配结构历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyAssemblyStructureSnapshot(
    input: HistoryPatchApplyInput<AssemblyStructureHistorySnapshot, AssemblyStructureHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists || !snapshot.data) {
        throw new Error(`装配结构 ${input.patch.entityId} 缺少历史快照。`);
    }

    const normalizedSnapshot = normalizeAssemblyStructureHistorySnapshot(snapshot.data);
    if (!isPlainAssemblyStructureHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`装配结构 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.stageAssemblyStructureHistoryRestore(normalizedSnapshot);
}

/**
 * 比较装配结构历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areAssemblyStructureSnapshotsEqual(
    left: HistoryEntitySnapshot<AssemblyStructureHistorySnapshot>,
    right: HistoryEntitySnapshot<AssemblyStructureHistorySnapshot>,
): boolean {
    return JSON.stringify(normalizeAssemblyStructureEntitySnapshot(left))
        === JSON.stringify(normalizeAssemblyStructureEntitySnapshot(right));
}

/**
 * 创建装配结构历史 adapter。
 *
 * @returns 装配结构历史 adapter。
 */
export function createAssemblyStructureHistoryAdapter(): HistoryDomainAdapter<
    AssemblyStructureHistorySnapshot,
    AssemblyStructureHistoryStore
> {
    return {
        adapterId: ASSEMBLY_STRUCTURE_HISTORY_ADAPTER_ID,
        kind: 'assembly',
        applySnapshot: applyAssemblyStructureSnapshot,
        areSnapshotsEqual: areAssemblyStructureSnapshotsEqual as HistoryDomainAdapter<
            AssemblyStructureHistorySnapshot,
            AssemblyStructureHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建装配结构历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 装配结构历史补丁。
 */
export function createAssemblyStructureHistoryPatch(
    patch: Omit<EntityPatch<AssemblyStructureHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<AssemblyStructureHistorySnapshot> {
    return {
        ...patch,
        before: normalizeAssemblyStructureEntitySnapshot(patch.before),
        after: normalizeAssemblyStructureEntitySnapshot(patch.after),
        entityKind: 'assembly',
        adapterId: ASSEMBLY_STRUCTURE_HISTORY_ADAPTER_ID,
    };
}
